/**
 * EduAnant facts, as stated on this site — the single source of truth.
 *
 * eduanant.cloud is the authority for everything here. These figures are copied from
 * its own data (`eduanant/src/data/pricing.ts`, `modules.ts`, `changelog.ts`,
 * `lib/timeline.ts` and `public/llms.txt`). When that site changes, change this file —
 * every EduAnant mention on snapxtechnologies.com reads from it.
 *
 * This file exists because the two sites drifted: this one said 14 modules, three
 * portals, 52 pages, "runs on your own server" and ₹50 flat, while eduanant.cloud said
 * 16 modules, two portals plus an app, 100+ screens, three ways to deploy and a rate
 * that depends on school size. A prospect reading both saw two different products.
 *
 * Rules for what goes in here:
 *   • Only claims eduanant.cloud also makes. Nothing marketing-only.
 *   • Prefer figures that do not go stale (no "latest version", no release month).
 *     Where something changes often, link to the page on eduanant.cloud that owns it.
 */

export const EDUANANT = {
  url: "https://eduanant.cloud",
  pricingUrl: "https://eduanant.cloud/pricing",
  featuresUrl: "https://eduanant.cloud/features",
  updatesUrl: "https://eduanant.cloud/updates",
  securityUrl: "https://eduanant.cloud/security",
  /** Demo sign-in details are issued on request; the demo host itself is a login page. */
  demoUrl: "https://eduanant.cloud/demo",
  playStoreUrl: "https://play.google.com/store/apps/details?id=cloud.eduanant.app",
  appName: "EduAnant",
  /**
   * Google Play figures, limited to what the public store listing shows, so any visitor
   * can check them. Source: Play Console, 19 Oct 2026 — 977 total installs (the listing
   * rounds down to "500+" until 1,000), lifetime rating 4.82. Update both from the Play
   * Console; when installs pass 1,000 the listing switches to "1K+".
   */
  playStore: { rating: "4.8", downloads: "500+" },

  modules: 16,
  screens: "100+",
  permissionKeys: 96,
  /** Two self-service portals plus the admin console and a native Android app. */
  portalsLine: "an admin console, Teacher and Student/Parent portals, and an Android app",

  /** Representative modules for short lists. The full set is on eduanant.cloud/features. */
  moduleHighlights: [
    "Admissions",
    "Fees",
    "Attendance",
    "Exams",
    "HR & staff attendance",
    "Library",
    "Transport",
    "Reception",
  ],

  pricing: {
    /** Entry rate, schools up to 250 students. Larger schools pay less per student. */
    annualRate: 40,
    monthlyRate: 50,
    lowestBandRate: 24,
    largeSchoolFrom: 1250,
    hostingAnnual: 18000,
    gstNote: "Prices exclude 18% GST.",
    included: "Setup, data migration from Excel, staff training and the Android app are included. No setup fee, no minimum.",
    founding: "Founding 10: the first ten schools get 50% off year one, with renewal rates locked for life.",
  },

  /** The three ways a school can run it — same product in each. */
  deployment: [
    { name: "One office PC", body: "No internet and nothing else to buy. One person uses it at a time." },
    { name: "Your school network", body: "Everyone on the school Wi-Fi uses it at once, and a broadband outage does not stop it." },
    { name: "Hosted by us", body: "We run it, with backups and updates, reachable from anywhere. ₹18,000 a year." },
  ],

  /** The live deployment. Figures as published on eduanant.cloud. */
  liveSchool: {
    name: "S.D.V. Public School, Patna",
    detail: "CBSE affiliated · three campuses",
    since: "April 2026",
    /** Our client since before Snapx was incorporated, and EduAnant's first customer. */
    clientSince: "2019",
    students: "1,300+",
    staff: "100+",
    url: "https://sdv-edu.in",
  },

  techStack: ["React", "TypeScript", "NestJS", "PostgreSQL", "Prisma", "React Native", "Docker"],
};

/** "16 modules, …" — the one-line description reused in prose and metadata. */
export const EDUANANT_SUMMARY = `${EDUANANT.modules} modules — admissions, fees, attendance, exams, HR, library, transport and more — with ${EDUANANT.portalsLine}`;
